import type {
  ComplianceReportResponse,
  ComplianceReportListResponse,
} from '@kora/types';
import { apiDownload, apiRequest } from './client.js';

/**
 * Compliance Reports API. Typed wrappers around /v1/compliance-reports*.
 * Contract is in apps/api/src/routes/compliance-reports.ts.
 *
 * Response types are now shared via @kora/types (promoted in PR ⑥b; they
 * were locally declared here in PR ⑥a-ui). Re-exported so existing page +
 * test imports from this module keep resolving.
 */
export type { ComplianceReportResponse, ComplianceReportListResponse };

// ---------------------------------------------------------------------------
// GET /v1/compliance-reports
// ---------------------------------------------------------------------------

export interface ListComplianceReportsArgs {
  periodYear?: number;
  periodMonth?: number;
  countryCode?: string;
  /** Server-side filter (PR ⑥b) — the run-detail panel uses this instead of
   *  fetching the org's reports and filtering client-side. */
  payrollRunId?: string;
}

export function listComplianceReports(
  args: ListComplianceReportsArgs = {},
): Promise<ComplianceReportListResponse> {
  const params = new URLSearchParams();
  if (args.periodYear) params.set('periodYear', String(args.periodYear));
  if (args.periodMonth) params.set('periodMonth', String(args.periodMonth));
  if (args.countryCode) params.set('countryCode', args.countryCode);
  if (args.payrollRunId) params.set('payrollRunId', args.payrollRunId);
  const qs = params.toString();
  return apiRequest<ComplianceReportListResponse>(
    `/v1/compliance-reports${qs ? `?${qs}` : ''}`,
  );
}

// ---------------------------------------------------------------------------
// GET /v1/compliance-reports/:id
// ---------------------------------------------------------------------------

export function getComplianceReport(id: string): Promise<ComplianceReportResponse> {
  return apiRequest<ComplianceReportResponse>(
    `/v1/compliance-reports/${encodeURIComponent(id)}`,
  );
}

// ---------------------------------------------------------------------------
// GET /v1/compliance-reports/:id/download — CSV file
// ---------------------------------------------------------------------------

/**
 * Trigger a browser download of the report CSV. Filename comes from the
 * server's Content-Disposition header; the fallback only applies if the
 * header is unexpectedly missing. Throws ApiError on non-2xx.
 */
export function downloadComplianceReport(id: string): Promise<void> {
  return apiDownload(
    `/v1/compliance-reports/${encodeURIComponent(id)}/download`,
    'compliance-report.csv',
  );
}
